import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";

type CoachNote = {
  id: string;
  subject_kind: "company" | "action_plan" | "finance" | "commercial" | "operations" | "hr" | "other";
  subject_id: string | null;
  title: string;
  content: string;
  status: "open" | "reviewed" | "archived";
  created_by: string;
  created_at: string;
  updated_at: string;
};

const subjectLabels: Record<CoachNote["subject_kind"], string> = {
  company: "Entreprise",
  action_plan: "Plan d’action",
  finance: "Finance",
  commercial: "Commercial",
  operations: "Opérations",
  hr: "Ressources humaines",
  other: "Autre",
};
const statusLabels: Record<CoachNote["status"], string> = {
  open: "À examiner",
  reviewed: "Examinée",
  archived: "Archivée",
};
const missingObject = (message = "") => /does not exist|schema cache|could not find|PGRST205|PGRST202/i.test(message);

export function CoachNotesPanel({ profileId, readOnlyCoach = false }: { profileId: string; readOnlyCoach?: boolean }) {
  const [notes, setNotes] = useState<CoachNote[]>([]),
    [available, setAvailable] = useState(true),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [editingId, setEditingId] = useState<string | null>(null),
    [subject, setSubject] = useState<CoachNote["subject_kind"]>("action_plan"),
    [title, setTitle] = useState(""),
    [content, setContent] = useState(""),
    [status, setStatus] = useState<CoachNote["status"]>("open");
  const load = useCallback(async () => {
    setLoading(true);
    const result = await supabase!.from("coach_notes").select("id,subject_kind,subject_id,title,content,status,created_by,created_at,updated_at").order("updated_at", { ascending: false });
    setLoading(false);
    if (result.error) {
      setAvailable(!missingObject(result.error.message));
      if (!missingObject(result.error.message)) setMessage(result.error.message);
      return;
    }
    setAvailable(true);
    setNotes((result.data || []) as CoachNote[]);
  }, []);
  useEffect(() => { void load(); }, [load]);
  const editableNotes = useMemo(() => new Set(notes.filter((note) => !readOnlyCoach || note.created_by === profileId).map((note) => note.id)), [notes, profileId, readOnlyCoach]);
  function reset() { setEditingId(null); setSubject("action_plan"); setTitle(""); setContent(""); setStatus("open"); }
  function edit(note: CoachNote) {
    if (!editableNotes.has(note.id)) return;
    setEditingId(note.id); setSubject(note.subject_kind); setTitle(note.title); setContent(note.content); setStatus(note.status); setMessage("");
  }
  async function save(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setMessage("");
    const result = await supabase!.rpc("save_coach_note", {
      p_note_id: editingId,
      p_subject_kind: subject,
      p_subject_id: subject === "action_plan" ? "action-plan-current" : null,
      p_title: title,
      p_content: content,
      p_status: status,
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : editingId ? "Note de coaching mise à jour et auditée." : "Note de coaching ajoutée et auditée.");
    if (!result.error) { reset(); await load(); }
  }
  if (!available) return <section className="panel coach-notes-panel"><div className="section-heading"><div><span className="eyebrow">Coaching</span><h2>Journal du coach</h2></div><span className="status-badge warning">Migration à appliquer</span></div></section>;
  return <section className="panel coach-notes-panel" data-testid="coach-notes-panel">
    <div className="section-heading"><div><span className="eyebrow">Coaching de l’entreprise</span><h2>Observations et recommandations</h2><p>Le Coach lit les espaces administrateur sans pouvoir modifier les données métier. Seul ce journal lui est ouvert en écriture.</p></div><span className="status-badge">{notes.filter((note) => note.status === "open").length} à examiner</span></div>
    <div className="coach-notes-layout">
      <div className="coach-note-list">{loading ? <p>Chargement…</p> : notes.map((note) => <article key={note.id}><div><span className="eyebrow">{subjectLabels[note.subject_kind]}</span><strong>{note.title}</strong><p>{note.content}</p><small>{statusLabels[note.status]} · mis à jour le {new Date(note.updated_at).toLocaleDateString("fr-FR")}</small></div>{editableNotes.has(note.id) && <button type="button" className="secondary compact" onClick={() => edit(note)}>Modifier</button>}</article>)}{!loading && !notes.length && <p className="empty">Aucune note de coaching.</p>}</div>
      <form className="coach-note-editor" onSubmit={save}><h3>{editingId ? "Modifier ma note" : "Ajouter une recommandation"}</h3><label>Sujet<select value={subject} onChange={(event) => setSubject(event.target.value as CoachNote["subject_kind"])}>{Object.entries(subjectLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label><label>Titre<input required minLength={3} maxLength={180} value={title} onChange={(event) => setTitle(event.target.value)} /></label><label>Observation ou recommandation<textarea required minLength={3} maxLength={8000} rows={6} value={content} onChange={(event) => setContent(event.target.value)} /></label><label>Suivi<select value={status} onChange={(event) => setStatus(event.target.value as CoachNote["status"])}>{Object.entries(statusLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label><div className="detail-actions">{editingId && <button type="button" className="secondary" onClick={reset}>Annuler</button>}<button disabled={busy}>{busy ? "Enregistrement…" : editingId ? "Enregistrer la note" : "Ajouter la note"}</button></div></form>
    </div>
    {message && <p className={`notice ${message.includes("auditée") ? "success" : "error"}`}>{message}</p>}
  </section>;
}
